/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/crmEntityMappers.ts
 * PURPOSE: Map RevOps, sales-opportunities, and data-management task payloads to CRM slice types and back
 * DEPENDENCIES: ../types/*, @shared/lib/frontend/api/opportunityApi (type-only imports avoided — use Record for API rows)
 * PUBLIC_API: mapSalesOpportunityRowToCrm, crmOpportunityToUpdateRequest, mapTaskApiToCrm, crmTaskToCreateRequest, mapRevopsActivityToCrm, crmActivityToLogInteractionBody, isUuid
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 440/450
 * RECENT: 2026-05-11 Task C — `mapSalesOpportunityRowToCrm` reads amount or deal_value, name or opportunity_name or title, owner display aliases, and stage or stage_slug or stage_name before normalize.
 * RECENT_PRIOR: 2026-05-11 Task F — `mapSalesOpportunityRowToCrm` resolves owner display names via `pickStr` then `MOCK_SCHEDULE_OWNERS` (same as task or activity mappers), so Deals Owner column does not show raw ids like user-1 when the API omits owner_name.
 * RECENT_PRIOR: 2026-05-01 **`mapSalesOpportunityRowToCrm`** reads MEDDPICC or 3 Whys or Value Framework strings (camel or snake keys)
 */

import type { Account } from '@shared/lib/frontend/api/accountApi';
import type { ManagedContact } from '@shared/lib/frontend/api/contactApi';
import type { Activity, ActivityStatus, ActivityType } from '../types/activity.types';
import { normalizeDealStageSlug } from '../constants/crmDealStages';
import { MOCK_SCHEDULE_OWNERS } from '../data/scheduleModalCatalog';
import type { Opportunity, OpportunitySource, OpportunityStage } from '../types/opportunity.types';
import { riskFlagsFromCrmRow } from '../utils/activityCoverage';
import type { Task, TaskPriority, TaskStatus, TaskType } from '../types/task.types';

const SOURCES: OpportunitySource[] = [
  'inbound',
  'outbound',
  'referral',
  'partner',
  'marketing',
  'unknown',
];

export function isUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
}

function pickStage(s: string | undefined): OpportunityStage {
  return normalizeDealStageSlug(s);
}

function pickSource(s: string | undefined): OpportunitySource {
  const v = (s ?? '').toLowerCase();
  return (SOURCES.find((x) => x === v) ?? 'unknown') as OpportunitySource;
}

function num(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') return Number(v) || 0;
  return 0;
}

function pickStr(row: Record<string, unknown>, ...aliases: string[]): string | undefined {
  for (const k of aliases) {
    const v = row[k];
    if (typeof v === 'string' && v.trim() !== '') return v;
  }
  return undefined;
}

/** Shell CRM: never surface **`user-*`** / **`usr_*`** tokens as a display name (GitHub #89). */
export const CRM_UNKNOWN_USER_LABEL = 'Unassigned';

function ownerDisplayNameFromRow(row: Record<string, unknown>, rawOwnerId: string, ...nameAliases: string[]): string {
  const nested = row.owner;
  let nestedName: string | undefined;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const n = (nested as { name?: unknown }).name;
    if (typeof n === 'string' && n.trim()) nestedName = n.trim();
  }
  const fromApi =
    (nameAliases.length ? pickStr(row, ...nameAliases) : undefined) ??
    nestedName ??
    MOCK_SCHEDULE_OWNERS.find((o) => o.id === rawOwnerId)?.name;
  if (fromApi && fromApi.trim()) return fromApi.trim();
  const id = rawOwnerId.trim();
  if (!id || /^user-\d+$/i.test(id) || /^usr_/i.test(id)) return CRM_UNKNOWN_USER_LABEL;
  // UUID owner with no resolved name from API — match backend Unassigned label.
  return CRM_UNKNOWN_USER_LABEL;
}

function ownerAvatarFromRow(row: Record<string, unknown>): string | undefined {
  const flat = pickStr(row, 'owner_avatar_url', 'ownerAvatarUrl', 'owner_display_avatar');
  if (flat) return flat;
  const nested = row.owner;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const a = (nested as { avatar?: unknown }).avatar;
    if (typeof a === 'string' && a.trim()) return a.trim();
  }
  return undefined;
}

/** Normalizes FastAPI or camelCase opportunity list rows into CRM `Opportunity`. */
export function mapSalesOpportunityRowToCrm(row: Record<string, unknown>): Opportunity {
  const id = String(row.id ?? '');
  // Shell CRM rows expose amount or expected_value; some sources use value or deal_value only.
  const value = num(row.value ?? row.expected_value ?? row.amount ?? row.deal_value);
  const rawProbability = num(row.probability);
  const closeRaw = (row.expected_close_date ?? row.close_date ?? row.closeDate) as string | undefined;
  const closeDateISO =
    closeRaw && String(closeRaw).length >= 10
      ? `${String(closeRaw).slice(0, 10)}T12:00:00.000Z`
      : new Date().toISOString();
  const created = String(row.created_at ?? row.createdAt ?? new Date().toISOString());
  const updated = String(row.updated_at ?? row.updatedAt ?? created);
  const rawOwnerId =
    row.owner_id != null
      ? String(row.owner_id).trim()
      : row.ownerId != null
        ? String(row.ownerId).trim()
        : '';
  const nestedOwner = row.owner as { id?: string } | undefined;
  const stableOwnerId = rawOwnerId || (nestedOwner?.id && String(nestedOwner.id).trim()) || '';
  const ownerName = ownerDisplayNameFromRow(
    row,
    stableOwnerId || rawOwnerId,
    'owner_name',
    'ownerName',
    'owner_display_name',
    'ownerDisplayName',
  );
  const ownerAvatarUrl = ownerAvatarFromRow(row);
  const apiStatus = String(row.status ?? 'active').toLowerCase();
  let stage = pickStage(
    String(
      row.stage ??
        row.stage_slug ??
        row.stageSlug ??
        pickStr(row, 'stage_name', 'stageName') ??
        ''
    )
  );
  let probability = rawProbability;
  if (apiStatus === 'won') {
    stage = 'closed_won';
    probability = 100;
  } else if (apiStatus === 'lost') {
    stage = 'closed_lost';
    probability = 0;
  }
  const weightedAmount =
    Number.isFinite(value) && Number.isFinite(probability) ? Math.round((value * probability) / 100) : 0;
  const fw: Record<string, string> = {};
  const addFw = (key: string, ...aliases: string[]) => {
    const v = pickStr(row, ...aliases);
    if (v !== undefined) fw[key] = v;
  };
  addFw('meddpiccMetrics', 'meddpiccMetrics', 'meddpicc_metrics');
  addFw('meddpiccEconomicBuyer', 'meddpiccEconomicBuyer', 'meddpicc_economic_buyer');
  addFw('meddpiccDecisionProcess', 'meddpiccDecisionProcess', 'meddpicc_decision_process');
  addFw('meddpiccDecisionCriteria', 'meddpiccDecisionCriteria', 'meddpicc_decision_criteria');
  addFw('meddpiccPaperProcess', 'meddpiccPaperProcess', 'meddpicc_paper_process');
  addFw('meddpiccIdentifiedPain', 'meddpiccIdentifiedPain', 'meddpicc_identified_pain');
  addFw('meddpiccChampion', 'meddpiccChampion', 'meddpicc_champion');
  addFw('meddpiccCompetition', 'meddpiccCompetition', 'meddpicc_competition');
  addFw('threeWhysAnything', 'threeWhysAnything', 'three_whys_anything');
  addFw('threeWhysYourCompany', 'threeWhysYourCompany', 'three_whys_your_company');
  addFw('threeWhysNow', 'threeWhysNow', 'three_whys_now');
  addFw('valueFrameworkCurrentSituation', 'valueFrameworkCurrentSituation', 'value_framework_current_situation');
  addFw(
    'valueFrameworkNegativeConsequences',
    'valueFrameworkNegativeConsequences',
    'value_framework_negative_consequences'
  );
  addFw(
    'valueFrameworkPositiveOutcomes',
    'valueFrameworkPositiveOutcomes',
    'value_framework_positive_outcomes'
  );
  addFw(
    'valueFrameworkRequiredCapabilities',
    'valueFrameworkRequiredCapabilities',
    'value_framework_required_capabilities'
  );
  addFw('valueFrameworkHowWeDoIt', 'valueFrameworkHowWeDoIt', 'value_framework_how_we_do_it');
  addFw(
    'valueFrameworkHowWeDoItBetter',
    'valueFrameworkHowWeDoItBetter',
    'value_framework_how_we_do_it_better'
  );
  addFw('valueFrameworkProofPoints', 'valueFrameworkProofPoints', 'value_framework_proof_points');
  const lostReason = pickStr(row, 'lostReason', 'lost_reason');
  const { activityCoverage, isAtRisk, isStuck } = riskFlagsFromCrmRow(row);
  const lastActivityRaw = pickStr(
    row,
    'last_activity_at',
    'lastActivityAt',
    'last_activity_date',
    'lastActivityDateISO'
  );
  return {
    id,
    name: String(pickStr(row, 'name', 'opportunityName', 'opportunity_name', 'title') ?? ''),
    accountId: String(row.account_id ?? row.accountId ?? ''),
    accountName: String(row.account_name ?? row.accountName ?? ''),
    primaryContactId:
      row.contact_id != null
        ? String(row.contact_id)
        : row.primaryContactId != null
          ? String(row.primaryContactId)
          : undefined,
    primaryContactName: String(row.contact_name ?? row.contactName ?? row.primaryContactName ?? ''),
    ownerId: stableOwnerId || rawOwnerId,
    ownerName,
    ...(ownerAvatarUrl ? { ownerAvatarUrl } : {}),
    backupOwnerId: pickStr(row, 'backupOwnerId', 'backup_owner_id') || null,
    backupOwnerName: pickStr(row, 'backupOwnerName', 'backup_owner_name', 'backup_owner_display_name') || '',
    stage,
    amount: value,
    weightedAmount,
    closeDateISO,
    probability,
    source: pickSource(String(row.source)),
    createdAtISO: created,
    updatedAtISO: updated,
    activityCoverage,
    isAtRisk,
    isStuck,
    ...(lastActivityRaw ? { lastActivityDateISO: lastActivityRaw } : {}),
    ...(lostReason !== undefined ? { lostReason } : {}),
    ...fw,
  };
}

export function crmOpportunityToUpdateRequest(patch: Partial<Opportunity>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (patch.name !== undefined) out.name = patch.name;
  if (patch.stage !== undefined) out.stage = patch.stage;
  if (patch.amount !== undefined) out.expected_value = patch.amount;
  if (patch.probability !== undefined) out.probability = patch.probability;
  if (patch.closeDateISO !== undefined && patch.closeDateISO.length >= 10) {
    out.expected_close_date = patch.closeDateISO.slice(0, 10);
  }
  if (patch.accountName !== undefined) out.account_name = patch.accountName;
  if (patch.ownerId !== undefined) {
    if (isUuid(patch.ownerId)) out.owner_id = patch.ownerId;
  }
  if (patch.primaryContactName !== undefined) out.contact_name = patch.primaryContactName;
  return out;
}

function apiTaskStatusToCrm(
  status: string,
  dueIso: string | undefined,
  completed: boolean | undefined
): TaskStatus {
  if (completed || status === 'done') return 'done';
  if (status === 'cancelled') return 'done';
  if (dueIso) {
    const d = Date.parse(dueIso);
    if (!Number.isNaN(d) && d < Date.now() && status === 'todo') return 'overdue';
  }
  if (status === 'in_progress') return 'in_progress';
  return 'todo';
}

export function mapTaskApiToCrm(row: Record<string, unknown>): Task {
  const due = (row.due_date as string | undefined) ?? new Date().toISOString();
  const statusRaw = String(row.status ?? 'todo');
  const completed = Boolean(row.completed);
  const status = apiTaskStatusToCrm(statusRaw, due, completed);
  const pr = String(row.priority ?? 'medium') as TaskPriority;
  const priority: TaskPriority =
    pr === 'low' || pr === 'medium' || pr === 'high' || pr === 'urgent' ? pr : 'medium';
  const rawOwnerId = String(row.assignee_id ?? row.user_id ?? '').trim();
  const ownerName = ownerDisplayNameFromRow(
    row,
    rawOwnerId,
    'assignee_name',
    'assigneeName',
    'owner_name',
    'ownerName',
    'user_name',
    'owner_display_name',
  );
  const ownerAvatarUrl = ownerAvatarFromRow(row);
  return {
    id: String(row.id ?? ''),
    title: String(row.title ?? ''),
    description: row.description != null ? String(row.description) : undefined,
    type: 'task' as TaskType,
    status,
    priority,
    ownerId: rawOwnerId || '',
    ownerName,
    ...(ownerAvatarUrl ? { ownerAvatarUrl } : {}),
    dueDateISO: due,
    createdAtISO: String(row.created_at ?? new Date().toISOString()),
    updatedAtISO: String(row.updated_at ?? row.created_at ?? new Date().toISOString()),
  };
}

export function crmTaskToCreateRequest(
  task: Omit<Task, 'id' | 'createdAtISO' | 'updatedAtISO'> & { id?: string }
): Record<string, unknown> {
  let status: 'todo' | 'in_progress' | 'done' | 'cancelled' = 'todo';
  if (task.status === 'done') status = 'done';
  else if (task.status === 'in_progress') status = 'in_progress';
  else if (task.status === 'overdue') status = 'todo';
  return {
    title: task.title,
    description: task.description,
    status,
    priority: task.priority,
    due_date: task.dueDateISO,
    assignee_id: isUuid(task.ownerId) ? task.ownerId : undefined,
  };
}

export function crmTaskToUpdateRequest(patch: Partial<Task>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (patch.title !== undefined) out.title = patch.title;
  if (patch.description !== undefined) out.description = patch.description;
  if (patch.dueDateISO !== undefined) out.due_date = patch.dueDateISO;
  if (patch.priority !== undefined) out.priority = patch.priority;
  if (patch.status !== undefined) {
    if (patch.status === 'done') out.status = 'done';
    else if (patch.status === 'in_progress') out.status = 'in_progress';
    else if (patch.status === 'overdue' || patch.status === 'todo') out.status = 'todo';
  }
  if (patch.ownerId !== undefined && isUuid(patch.ownerId)) out.assignee_id = patch.ownerId;
  return out;
}

function mapActivityType(t: string | undefined): ActivityType {
  const v = (t ?? '').toLowerCase();
  if (v === 'call' || v === 'email' || v === 'meeting' || v === 'note' || v === 'task') return v;
  if (v === 'demo' || v === 'proposal_sent' || v === 'contract_sent') return 'meeting';
  return 'note';
}

function mapRelatedType(t: string | undefined): Activity['relatedEntityType'] {
  const v = (t ?? '').toLowerCase();
  if (v === 'company') return 'account';
  if (v === 'deal') return 'deal';
  if (v === 'contact') return 'contact';
  return 'deal';
}

function mapActivityStatusFromRevops(_row: Record<string, unknown>): ActivityStatus {
  return 'open';
}

/** Maps data-management RevOps activity list item to CRM `Activity`. */
export function mapRevopsActivityToCrm(row: Record<string, unknown>): Activity {
  const created = String(row.created_at ?? new Date().toISOString());
  const updated = String(row.updated_at ?? created);
  const type = mapActivityType(String(row.activity_type));
  const subject =
    (row.outcome != null && String(row.outcome).trim() !== ''
      ? String(row.outcome)
      : String(row.activity_type ?? 'Activity')) +
    (row.activity_subtype ? ` — ${String(row.activity_subtype)}` : '');
  // Same display-name resolution as mapTaskApiToCrm: API name field → catalog → id.
  const rawOwnerId = String(row.user_id ?? '');
  const resolvedOwner = MOCK_SCHEDULE_OWNERS.find((o) => o.id === rawOwnerId);
  return {
    id: String(row.id ?? ''),
    type,
    subject,
    relatedEntityId: String(row.related_to_id ?? ''),
    relatedEntityType: mapRelatedType(String(row.related_to_type)),
    relatedEntityName: '',
    ownerId: rawOwnerId,
    ownerName:
      pickStr(row, 'user_name', 'owner_name', 'assignee_name') ??
      resolvedOwner?.name ??
      rawOwnerId,
    dueAtISO: created,
    status: mapActivityStatusFromRevops(row),
    outcome: row.outcome != null ? String(row.outcome) : undefined,
    createdAtISO: created,
    updatedAtISO: updated,
  };
}

const LOG_TYPES = new Set([
  'email',
  'call',
  'meeting',
  'demo',
  'proposal_sent',
  'contract_sent',
  'follow_up',
  'risk_mitigation',
  'research',
  'internal_sync',
]);

function crmTypeToLogType(t: ActivityType): string {
  if (t === 'note') return 'internal_sync';
  if (t === 'task') return 'follow_up';
  if (LOG_TYPES.has(t)) return t;
  return 'internal_sync';
}

function crmRelatedToApi(t: Activity['relatedEntityType']): string {
  if (t === 'account') return 'company';
  if (t === 'territory') return 'company';
  return t === 'deal' ? 'deal' : 'contact';
}

/** Maps shell CRM GET /contacts row to data-management `ManagedContact` shape. */
export function mapCrmShellContactToManaged(row: Record<string, unknown>): ManagedContact {
  const tags = Array.isArray(row.tags) ? (row.tags as unknown[]).map((t) => String(t)) : [];
  const rawOid = String(row.ownerId ?? row.owner_id ?? '').trim();
  const owner_display_name = ownerDisplayNameFromRow(row, rawOid, 'ownerName', 'owner_name', 'owner_display_name');
  return {
    id: String(row.id ?? ''),
    tenant_id: String(row.tenantId ?? row.tenant_id ?? ''),
    user_id: rawOid || String(row.ownerId ?? row.owner_id ?? ''),
    owner_display_name,
    territory_id: row.territoryId != null ? String(row.territoryId) : row.territory_id != null ? String(row.territory_id) : undefined,
    preferred_contact_method:
      row.preferredContactMethod != null
        ? String(row.preferredContactMethod)
        : row.preferred_contact_method != null
          ? String(row.preferred_contact_method)
          : undefined,
    first_name: String(row.firstName ?? row.first_name ?? ''),
    last_name: String(row.lastName ?? row.last_name ?? ''),
    email: row.email != null ? String(row.email) : undefined,
    phone: row.phone != null ? String(row.phone) : undefined,
    company: row.companyName != null ? String(row.companyName) : undefined,
    job_title:
      row.jobTitle != null ? String(row.jobTitle) : row.job_title != null ? String(row.job_title) : undefined,
    address: undefined,
    tags,
    notes: row.notes != null ? String(row.notes) : undefined,
    created_by_ai: false,
    created_at: String(row.createdAtISO ?? row.created_at ?? ''),
    updated_at: String(row.updatedAtISO ?? row.updated_at ?? ''),
  };
}

/** Maps shell CRM GET /accounts row to sales `Account` list shape. */
export function mapCrmShellAccountToAccount(row: Record<string, unknown>): Account {
  const emp =
    typeof row.employee_count === 'number'
      ? row.employee_count
      : row.size != null && String(row.size).trim() !== '' && /^\d+$/.test(String(row.size))
        ? Number(row.size)
        : undefined;
  return {
    id: String(row.id ?? ''),
    tenant_id: String(row.tenant_id ?? ''),
    name: String(row.name ?? ''),
    industry: row.industry != null ? String(row.industry) : undefined,
    website: row.website != null ? String(row.website) : undefined,
    employee_count: emp,
    status: String(row.status ?? 'active'),
    source: 'crm',
    sales_account_id:
      pickStr(row, 'salesAccountId', 'sales_account_id') ?? null,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  };
}

/** Maps shell CRM GET /activities row to CRM `Activity`. */
export function mapCrmShellActivityToCrm(row: Record<string, unknown>): Activity {
  const created = String(row.created_at ?? row.createdAtISO ?? new Date().toISOString());
  const updated = String(row.updated_at ?? row.updatedAtISO ?? created);
  const dueRaw = row.due_date ?? row.dueAtISO;
  const dueAtISO = dueRaw != null && String(dueRaw).length > 0 ? String(dueRaw) : created;
  const type = mapActivityType(String(row.type ?? 'note'));
  const stRaw = String(row.status ?? 'open').toLowerCase();
  let status: ActivityStatus = 'open';
  if (stRaw === 'completed' || stRaw === 'complete') status = 'complete';
  else if (stRaw === 'scheduled') status = 'scheduled';
  else if (stRaw === 'overdue') status = 'overdue';
  const metaRaw = row.metadata ?? row.activity_metadata;
  let metadata: Activity['metadata'];
  if (metaRaw != null && typeof metaRaw === 'object' && !Array.isArray(metaRaw)) {
    const m = metaRaw as Record<string, unknown>;
    const dur = m.durationSeconds;
    const durationSeconds =
      typeof dur === 'number' && Number.isFinite(dur) ? dur : typeof dur === 'string' ? Number(dur) || 0 : 0;
    const tr = m.transcript;
    const transcript = Array.isArray(tr) ? tr.map((x) => String(x)) : undefined;
    metadata = {
      outcome: String(m.outcome ?? ''),
      durationSeconds,
      transcript,
    };
  }

  const rawOwnerId = String(row.assigned_to ?? row.owner_id ?? row.ownerId ?? '').trim();
  const ownerName = ownerDisplayNameFromRow(
    row,
    rawOwnerId,
    'assigned_to_name',
    'owner_name',
    'ownerName',
    'assignee_name',
    'owner_display_name',
  );
  const ownerAvatarUrl = ownerAvatarFromRow(row);
  return {
    id: String(row.id ?? ''),
    type,
    subject: String(row.subject ?? row.notes ?? row.description ?? 'Activity'),
    relatedEntityId: String(row.related_entity_id ?? row.relatedEntityId ?? ''),
    relatedEntityType: mapRelatedType(String(row.related_entity_type ?? row.relatedEntityType ?? 'deal')),
    relatedEntityName: '',
    ownerId: rawOwnerId,
    ownerName,
    ...(ownerAvatarUrl ? { ownerAvatarUrl } : {}),
    dueAtISO,
    status,
    notes: row.notes != null ? String(row.notes) : row.description != null ? String(row.description) : undefined,
    outcome: row.outcome != null ? String(row.outcome) : undefined,
    metadata,
    createdAtISO: created,
    updatedAtISO: updated,
  };
}

export function crmActivityToLogInteractionBody(data: Partial<Activity>): Record<string, unknown> | null {
  const relId = data.relatedEntityId;
  const relType = data.relatedEntityType;
  if (!relId || !relType) return null;
  return {
    activity_type: crmTypeToLogType((data.type ?? 'note') as ActivityType),
    activity_subtype: data.subject,
    related_to_type: crmRelatedToApi(relType),
    related_to_id: relId,
    outcome: data.outcome ?? data.notes ?? null,
    sentiment: 'neutral',
    participants: [],
    duration_minutes: null,
  };
}
